// Catalog.ID Client — Registration Payment Wire Contract
// ============================================================================
// **Duplicated** in publisher/src/worker/registration-types.ts — the two
// projects have separate tsconfigs with no cross-project imports. Any
// wire-format change must update BOTH files in the same PR.

/**
 * What is being paid for. Each maps to its OWN Stripe price
 * (registration-restructure §13.4): `delegate` is the invitation slot an
 * organization buys in advance, priced separately from the two registrations.
 */
export type RegistrationType = 'personal' | 'organization' | 'delegate';

/**
 * The batch cap (registration-wallets D24). Bits ride every voucher, so one
 * checkout mints up to 50 locks inside one webhook — well inside the Worker's
 * subrequest budget, and the reason the old cap of 100 is gone. Enforced at
 * `create-payment`, again at the webhook against the session metadata, and a
 * third time by the CID server on the signed `quantity` claim.
 */
export const MAX_VOUCHER_QUANTITY = 25;

/**
 * What the buyer's client commits to for ONE voucher before the card is
 * charged (registration-wallets §4.3).
 *
 * The code itself is minted on this device and never leaves it (D16): the
 * Worker is handed the lock key and the public key derived from it, and
 * `codeHash` — the value the CID SERVER will store as `voucher.codehash` — only
 * so the proof can carry it as a signed commitment. Neither service can go from
 * any of the three back to the code.
 */
export interface VoucherLockSpec {
    /** `voucherCodeHash(lookupIdHex)` — 64 lowercase hex. Forwarded verbatim
     *  into the proof's `voucherHashes[]`; the Worker never learns the lookup
     *  id it hashes. */
    codeHash: string;
    /** `lockKeyFor(publicKey, 0, routingPrefix)` — the ACCOUNT lock (slot 0, §4.1). */
    lockKey: string;
    /** The voucher keypair's public half, 64 lowercase hex: the lock's only
     *  condition at creation. */
    publicKey: string;
    /** Organization vouchers only — `lockKeyFor(publicKey, 1, routingPrefix)`, the
     *  FOUNDER lock (slot 1). One keypair serves both locks, so there is no
     *  second public key to carry; `founderPublicKey` repeats it for the
     *  Worker's per-lock validation. Refused on the other two types. */
    founderLockKey?: string;
    founderPublicKey?: string;
}

export interface CreatePaymentRequest {
    registrationType: RegistrationType;
    nonce: string;
    successUrl: string;
    cancelUrl: string;
    /**
     * How many registration vouchers this one payment buys. Absent means 1.
     * Must be a whole number in 1..{@link MAX_VOUCHER_QUANTITY}, and
     * `vouchers` must carry exactly that many entries.
     */
    quantity?: number;
    /**
     * Bits parked on EVERY voucher of this order (D1) — mandatory, since a
     * registration without bits is the free-starter idea the whitepaper rules
     * out. Bounded by the Worker's `bits-config`; the CID server mirrors the
     * range for display only and never learns the value.
     */
    bits: number;
    /** Organization orders only: the bits of the SECOND lock, the founding
     *  delegate's (§4.13). Refused on personal and delegate orders. */
    founderBits?: number;
    /** Exactly `quantity` entries, minted before the checkout opens. */
    vouchers: VoucherLockSpec[];
}

/**
 * `GET /api/checkout/bits-config` — the Worker's own bounds plus the rate the
 * storefront turns bits into a euro figure with. The Worker ENFORCES these; the
 * CID server's `/api/registration/config` mirrors the first three for the case
 * where the Worker is unreachable, and carries no rate at all.
 */
export interface BitsConfigResponse {
    min: number;
    max: number;
    default: number;
    /** 5 000 bits = €1 today. Display only — no client renders a total. */
    bitsPerEuro: number;
}

export interface CreatePaymentResponse {
    checkoutUrl: string;
    registrationId: string;
}

export type PaymentStatusResponse =
    | { status: 'pending' }
    | { status: 'complete'; proofToken: string }
    | { status: 'error'; error: string };
